/**
 * Origin parsing, canonicalization and validation.
 *
 * This lives in contracts because THREE places need to agree on what an origin
 * is: the backend's per-instance CORS matcher (which compares a browser's
 * `Origin` header against the stored allowlist), the API's write path (which
 * should refuse an entry that can never match), and the dashboard's editors
 * (which should say so before the operator saves).
 *
 * They did not agree before. An entry like `app.example.com` — no scheme — was
 * accepted by the API, rendered as a chip in the dashboard, and then silently
 * failed every comparison, because a browser's `Origin` is always
 * `scheme://host[:port]`. The widget's only symptom was a 403 it could not
 * explain, so the site simply never sent feedback.
 *
 * Matching is deliberately strict: no substring or suffix matching, ever
 * (`evil-example.com` must never be admitted by an `example.com` entry). The
 * only leniency is canonicalization — case, one trailing slash, and a default
 * port — applied identically to both sides of the comparison.
 */
import { array, string } from 'zod';

/**
 * `URL` is a standard global in every runtime this package targets — the browser
 * (dashboard) and Node (backend) alike — but TypeScript only declares it in the
 * DOM and `@types/node` libs, and contracts deliberately has neither
 * (`lib: ["ES2023"]`, `types: []`) so it can never accidentally bind to one
 * environment. Declaring exactly the surface used here keeps that property; a
 * module-scoped `declare` is erased at compile time and resolves to the real
 * global at runtime.
 */
interface ParsedUrl {
  protocol: string;
  hostname: string;
  host: string;
  port: string;
  pathname: string;
  search: string;
  hash: string;
  username: string;
  password: string;
}
declare const URL: new (input: string) => ParsedUrl;

/** The single allowlist entry meaning "any origin". */
export const ORIGIN_WILDCARD = '*';

/**
 * Canonicalize an origin for comparison, or `null` if it is not one.
 *
 * Lower-cases scheme + host, drops a single trailing slash, and elides the
 * scheme's default port, so `HTTPS://X.com:443/` === `https://x.com`.
 */
export function normalizeOrigin(value: string | undefined | null): string | null {
  if (!value) return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  // Drop a single trailing slash (an origin has no path, but be lenient — this
  // is the single most common way an origin is pasted out of a browser bar).
  const candidate = trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
  let url: ParsedUrl;
  try {
    url = new URL(candidate);
  } catch {
    return null;
  }
  // An origin carries no path/query/fragment/credentials; reject anything that
  // does, so a stored "https://x.com/app" cannot be coerced into matching x.com.
  if (url.pathname !== '/' && url.pathname !== '') return null;
  if (url.search || url.hash || url.username || url.password) return null;
  // No default-port table needed: the URL parser already omits a scheme's
  // default port for every special scheme (http/https/ws/wss), so `url.port` is
  // empty for `https://x.com:443` and retained for `http://x.com:443`.
  const scheme = url.protocol.toLowerCase();
  const host = url.hostname.toLowerCase();
  return `${scheme}//${host}${url.port ? `:${url.port}` : ''}`;
}

/**
 * Explain, in the operator's terms, why `value` is not a usable origin.
 *
 * Only called for values `normalizeOrigin` already rejected, so every branch
 * here corresponds to a real rejection reason.
 */
export function describeOriginProblem(value: string): string {
  const trimmed = value.trim();
  const shown = JSON.stringify(trimmed);

  let parsed: ParsedUrl | null;
  try {
    parsed = new URL(trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed);
  } catch {
    parsed = null;
  }

  if (parsed) {
    // It parsed but carried more than an origin. Exactly one of these applies.
    if (parsed.pathname !== '/' && parsed.pathname !== '') {
      return `${shown} is not an origin — drop the path`;
    }
    if (parsed.search || parsed.hash) {
      return `${shown} is not an origin — drop the query string and fragment`;
    }
    return `${shown} is not an origin — drop the username and password`;
  }

  // Did not parse at all. Overwhelmingly this is a missing scheme.
  try {
    const guess = new URL(`https://${trimmed}`);
    return `${shown} is not an origin — include the scheme, e.g. ${guess.protocol}//${guess.host}`;
  } catch {
    return `${shown} is not an origin — expected scheme://host[:port]`;
  }
}

/**
 * One entry of `allowedOrigins`: either the explicit wildcard, or an origin.
 *
 * Note the `transform` — entries are STORED canonical. That means a pasted
 * `https://App.Example.com/` is saved as `https://app.example.com`, an existing
 * sloppy row self-heals the next time an operator saves, and the per-request
 * `normalizeOrigin(entry)` in the CORS matcher becomes a no-op in the common
 * case.
 */
export const AllowedOriginSchema = string()
  .trim()
  .min(1)
  .max(2048)
  .superRefine((value, ctx) => {
    if (value === ORIGIN_WILDCARD) return;
    if (normalizeOrigin(value) === null) {
      ctx.addIssue({ code: 'custom', message: describeOriginProblem(value) });
    }
  })
  .transform((value) =>
    value === ORIGIN_WILDCARD ? ORIGIN_WILDCARD : (normalizeOrigin(value) as string),
  );

/** The `allowedOrigins` list as accepted on a write. */
export const AllowedOriginsSchema = array(AllowedOriginSchema).max(100);
